/* cv-load-more.js — N7 wayfinding (w80, 2026-09-02)
 * <cv-load-more> appends the next page of a collection / search grid in place.
 *   · fetch = the Section Rendering API call facets.js already makes
 *     (`<pathname>?<params>&page=N&section_id=<grid section>`), so filters and sort ride along;
 *   · desktop: auto-load while the control nears the viewport until 96 products are shown,
 *     then a click, each click allowing 72 more before asking again; mobile: click only
 *     (Baymard, number-of-items-loaded-by-default);
 *   · the clicked card is remembered in sessionStorage `cv:plp`; on back/forward the pages
 *     are re-appended until that card exists, then it is scrolled to centre — the back
 *     button lands where the shopper left. The same record feeds the PDP's history crumb.
 *   · appended <li>s reveal through their own IntersectionObserver: re-observing already
 *     shown cards through Dawn's observer would hide them (animations.js adds
 *     scroll-trigger--offscreen to every non-intersecting element it is handed).
 */
(function () {
  'use strict';
  var KEY = 'cv:plp';
  var DESKTOP_AUTO = 96;
  var CLICK_EXTENDS = 72;
  var MOBILE = window.matchMedia('(max-width: 749px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function listKey() {
    var u = new URL(location.href);
    u.searchParams.delete('page');
    return u.pathname + u.search;
  }
  function listTitle() {
    var h = document.querySelector('main h1');
    var t = '';
    if (h) {
      // Dawn prefixes the shelf h1 with a visually-hidden "Collection:" — read what is seen
      var c = h.cloneNode(true);
      Array.prototype.forEach.call(c.querySelectorAll('.visually-hidden'), function (n) { n.remove(); });
      t = c.textContent.replace(/\s+/g, ' ').trim();
    }
    if (!t && location.pathname.indexOf('/search') === 0) t = 'search results';
    return t.length > 48 ? t.slice(0, 45) + '…' : t || 'results';
  }
  function read() {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (e) { return null; }
  }
  function save(patch) {
    try {
      var s = read() || {};
      if (s.k !== patch.k) s = {};
      Object.keys(patch).forEach(function (k) { s[k] = patch[k]; });
      s.ts = Date.now();
      sessionStorage.setItem(KEY, JSON.stringify(s));
    } catch (e) { /* private mode: no restore, nothing else breaks */ }
  }
  function handleOf(a) {
    return ((a.pathname || '').split('/products/')[1] || '').split('/')[0];
  }
  function navType() {
    var e = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    return e ? e.type : '';
  }

  class CvLoadMore extends HTMLElement {
    connectedCallback() {
      var container = this.closest('#ProductGridContainer') || document;
      this.list = container.querySelector('ul.product-grid');
      var grid = document.getElementById('product-grid');
      this.sectionId = grid && grid.dataset.id;
      if (!this.list || !this.sectionId) return;
      this.page = +this.dataset.page || 1;
      this.pages = +this.dataset.pages || 1;
      this.total = +this.dataset.total || 0;
      this.seen = +this.dataset.seen || this.list.children.length;
      this.busy = false;
      this.autoUntil = MOBILE.matches ? 0 : Math.min(this.total, DESKTOP_AUTO);
      this.bindButton();
      this.bindCards();
      this.watch();
      var self = this;
      var prior = read();
      var comingBack = (navType() === 'back_forward' || navType() === 'reload')
        && prior && prior.k === listKey() && prior.h && prior.pages > this.page;
      save({ k: listKey(), pages: this.page, t: listTitle(), u: location.href });
      if (comingBack) {
        // the browser's own restoration would scroll a 24-card page to a 72-card offset;
        // we append the pages first, then place the clicked card ourselves
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        this.restore().then(function () {
          if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
          self.watch();
        });
      }
    }

    bindButton() {
      var self = this;
      this.btn = this.querySelector('[data-cv-next]');
      if (!this.btn) return;
      this.btn.addEventListener('click', function (e) {
        e.preventDefault();
        self.autoUntil = self.seen + CLICK_EXTENDS;
        self.more(true);
      });
    }

    bindCards() {
      if (this.list.__cvBound) return;
      this.list.__cvBound = true;
      var self = this;
      this.list.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[href*="/products/"]');
        if (!a) return;
        save({ k: listKey(), pages: self.page, h: handleOf(a), y: window.scrollY, t: listTitle(), u: location.href });
      });
    }

    watch() {
      if (this.io) this.io.disconnect();
      if (!('IntersectionObserver' in window) || this.autoUntil <= this.seen || this.page >= this.pages) return;
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        if (!entries.some(function (en) { return en.isIntersecting; })) return;
        if (self.seen >= self.autoUntil || self.page >= self.pages) { self.io.disconnect(); return; }
        self.more(false);
      }, { rootMargin: '600px 0px' });
      this.io.observe(this);
    }

    more(byClick) {
      var self = this;
      if (this.busy || this.page >= this.pages) return Promise.resolve(false);
      this.busy = true;
      this.classList.add('cv-more--loading');
      if (this.btn) { this.btn.setAttribute('aria-busy', 'true'); this.btn.textContent = 'Loading ' + Math.min(+this.dataset.size || 24, this.total - this.seen) + ' more…'; }
      var next = this.page + 1;
      var u = new URL(location.href);
      u.searchParams.set('page', String(next));
      u.searchParams.set('section_id', this.sectionId);
      return fetch(u.toString(), { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newList = doc.querySelector('ul.product-grid');
          if (!newList) throw new Error('no grid in page ' + next);
          var items = Array.prototype.slice.call(newList.children);
          var frag = document.createDocumentFragment();
          items.forEach(function (li, i) {
            li.classList.remove('scroll-trigger--cancel');
            if (li.classList.contains('scroll-trigger')) {
              li.classList.add('scroll-trigger--offscreen');
              if (li.hasAttribute('data-cascade')) li.style.setProperty('--animation-order', String(i % 4));
            }
            frag.appendChild(li);
          });
          self.list.appendChild(frag);
          self.reveal(items);
          self.page = next;
          self.seen = Math.min(self.total, self.seen + items.length);
          self.paint(doc.querySelector('cv-load-more'));
          save({ k: listKey(), pages: self.page });
          if (byClick && items[0]) {
            // the card's first /products/ link is Dawn's 0×0 no-media link and refuses
            // focus; walk until one takes it (the heading link does)
            var links = items[0].querySelectorAll('a[href*="/products/"]');
            for (var i = 0; i < links.length; i++) {
              links[i].focus({ preventScroll: true });
              if (document.activeElement === links[i]) break;
            }
          }
          return true;
        })
        .catch(function () {
          // the network said no: hand over to the real next-page link
          if (byClick && self.btn) location.href = self.btn.href;
          return false;
        })
        .then(function (ok) {
          self.busy = false;
          self.classList.remove('cv-more--loading');
          if (self.btn) self.btn.removeAttribute('aria-busy');
          // re-arm: observe() fires once with the current state, so a control still
          // inside the margin after a short append keeps loading instead of stalling
          if (ok && !self.restoring) self.watch();
          return ok;
        });
    }

    paint(fresh) {
      if (!fresh) { this.remove(); return; }
      this.innerHTML = fresh.innerHTML;
      var self = this;
      ['page', 'seen'].forEach(function (k) { self.dataset[k] = fresh.dataset[k]; });
      this.bindButton();
      var bar = this.querySelector('[data-cv-bar]');
      if (bar && !REDUCED.matches) {
        var w = bar.style.width;
        bar.style.width = (100 * (this.seen - (+this.dataset.size || 24)) / this.total) + '%';
        requestAnimationFrame(function () { bar.style.width = w; });
      }
    }

    reveal(items) {
      var pending = items.filter(function (li) { return li.classList.contains('scroll-trigger--offscreen'); });
      if (!pending.length) return;
      if (REDUCED.matches || !('IntersectionObserver' in window)) {
        pending.forEach(function (li) { li.classList.remove('scroll-trigger--offscreen'); });
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          en.target.classList.remove('scroll-trigger--offscreen');
          io.unobserve(en.target);
        });
      }, { rootMargin: '0px 0px -50px 0px' });
      pending.forEach(function (li) { io.observe(li); });
    }

    restore() {
      var s = read();
      var self = this;
      if (!s || s.k !== listKey() || !s.h || Date.now() - (s.ts || 0) > 36e5) return Promise.resolve(false);
      var sel = 'a[href*="/products/' + s.h + '"]';
      var cap = Math.min(this.pages, this.page + 12);
      this.restoring = true;
      function done(found) { self.restoring = false; return found; }
      function step() {
        var a = self.list.querySelector(sel);
        if (a) {
          self.autoUntil = Math.max(self.autoUntil, self.seen);
          var li = a.closest('li') || a;
          li.classList.remove('scroll-trigger--offscreen');
          requestAnimationFrame(function () { li.scrollIntoView({ block: 'center', behavior: 'auto' }); });
          return Promise.resolve(done(true));
        }
        if (self.page >= cap) return Promise.resolve(done(false));
        return self.more(false).then(function (ok) { return ok ? step() : done(false); });
      }
      return step();
    }
  }

  if (!customElements.get('cv-load-more')) customElements.define('cv-load-more', CvLoadMore);
})();
